import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
  CircularProgress,
} from "@mui/material";
import { getLatLng } from "use-places-autocomplete";
import distanceBetween from "../../utils/distanceBetween";
import { useAppConfig } from "../../context/AppConfigContext";
import GooglePlacesAutocomplete from "../../components/GooglePlacesAutocomplete";

const DeliveryMethodForm = ({
  method,
  setMethod,
  selectedLocation,
  setSelectedLocation,
  address,
  setAddress,
  setIsDeliveryValid,
}) => {
  const { config, loading, error: configError } = useAppConfig();
  const [error, setError] = useState("");
  const [coords, setCoords] = useState(null);
  const [isValidLocal, setIsValidLocal] = useState(false);
  const [addressConfirmed, setAddressConfirmed] = useState(false);

  useEffect(() => {
    if (method === "pickup") {
      const valid = !!selectedLocation;
      setIsValidLocal(valid);
      setIsDeliveryValid?.(valid);
    } else {
      const valid = !!address && isValidLocal;
      setIsDeliveryValid?.(valid);
    }
  }, [method, address, selectedLocation, isValidLocal]);

  const handleAddressSelect = async (description, placeDetails) => {
    try {
      if (!placeDetails || !placeDetails.geometry?.location) {
        setError("Invalid address selected.");
        setIsValidLocal(false);
        setIsDeliveryValid?.(false);
        return;
      }

      const lat = placeDetails.geometry.location.lat();
      const lng = placeDetails.geometry.location.lng();

      setCoords({ lat, lng });
      setAddress(description);

      const dist = distanceBetween(
        lat,
        lng,
        config.delivery.storeCoordinates.lat,
        config.delivery.storeCoordinates.lng
      );

      if (dist > config.delivery.deliveryRadiusKm) {
        setError("Address is outside our delivery area.");
        setIsValidLocal(false);
        setIsDeliveryValid?.(false);
      } else {
        setError("");
        setIsValidLocal(true);
        setIsDeliveryValid?.(true);
      }
    } catch (e) {
      console.error("Failed to process selected address:", e);
      setError("Could not verify location.");
      setIsValidLocal(false);
      setIsDeliveryValid?.(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (configError || !config?.delivery) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load delivery settings.
      </Alert>
    );
  }

  const { deliveryFee, pickupLocations, isAvailable } = config.delivery;

  return (
    <Box>
      <FormControl component="fieldset">
        <FormLabel>Choose how you'd like to receive your order:</FormLabel>
        <RadioGroup
          row
          value={method}
          onChange={(e) => setMethod(e.target.value)}
          sx={{ my: 2 }}>
          <FormControlLabel value="pickup" control={<Radio />} label="Pickup" />
          <FormControlLabel
            value="delivery"
            control={<Radio />}
            label={`Delivery (+$${deliveryFee.toFixed(2)})`}
            disabled={!isAvailable}
          />
        </RadioGroup>
        {!isAvailable && method !== "pickup" && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 1.5, color: "gray" }}>
            Delivery service for the general public is temporarily unavailable.
            For large or corporate orders please contact us
          </Typography>
        )}
      </FormControl>

      {method === "pickup" && (
        <Box sx={{ mt: 2 }}>
          <FormLabel>Select Pickup Location</FormLabel>
          <Box sx={{ display: "flex", gap: 2, mt: 1, flexWrap: "wrap" }}>
            {pickupLocations.map((loc, idx) => (
              <Box
                key={idx}
                onClick={() => setSelectedLocation(loc)}
                sx={{
                  px: 2,
                  py: 1.5,
                  border: "1px solid",
                  borderColor:
                    selectedLocation === loc ? "primary.main" : "grey.300",
                  borderRadius: 2,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 500,
                  color:
                    selectedLocation === loc ? "primary.main" : "text.primary",
                  transition: "all 0.2s ease-in-out",
                  boxShadow:
                    selectedLocation === loc
                      ? "0 0 0 1px rgba(0,0,0,0.05)"
                      : "none",
                  "&:hover": {
                    boxShadow: "0 0 0 2px rgba(0,0,0,0.05)",
                  },
                }}>
                {loc}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {method === "delivery" && isAvailable && (
        <Box sx={{ mt: 3 }}>
          <FormLabel>Enter Delivery Address</FormLabel>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            We currently deliver within a {config.delivery.deliveryRadiusKm}km
            radius of our store.
          </Typography>

          <GooglePlacesAutocomplete
            label="Start typing..."
            value={address}
            onChange={(val) => {
              setAddress(val);
              setAddressConfirmed(false);
              setIsValidLocal(false);
              setIsDeliveryValid?.(false);
            }}
            onSelect={(description, placeDetails) => {
              setAddressConfirmed(true);
              handleAddressSelect(description, placeDetails);
            }}
            confirmed={addressConfirmed}
            showWarningIfUnconfirmed={true}
          />

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default DeliveryMethodForm;
